import { packageLabel } from '@/lib/realEstatePackages';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AddClientDialog } from '@/components/admin/AddClientDialog';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Filter, ChevronLeft, ChevronRight, Home, Camera, Package, Calendar, User, Clock, DollarSign, ExternalLink, ImageIcon, UserPlus, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Status config
const STATUS_CONFIG = {
  requested: { label: 'Requested', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  shot: { label: 'Shot', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  editing: { label: 'Editing', className: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  paid: { label: 'Paid', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  // Legacy
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  received: { label: 'Received', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
};


function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPrice(cents, isCustomQuote) {
  if (isCustomQuote && !cents) return 'Custom Quote';
  if (!cents) return '—';
  return `$${(cents / 100).toFixed(0)}`;
}

function formatOrderNumber(orderId) {
  if (!orderId) return 'N/A';
  const shortId = orderId.split('-')[0].toUpperCase();
  return `RE-${shortId}`;
}

export default function AdminOrders() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [serviceFilter, setServiceFilter] = useState(searchParams.get('service') || 'real_estate');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Check admin access
  const isAdmin = user?.roles?.includes('admin');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch orders
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;

    const fetchOrders = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set('status', statusFilter);
        if (serviceFilter) params.set('service_type', serviceFilter);
        if (debouncedSearch) params.set('search', debouncedSearch);
        params.set('page', searchParams.get('page') || '1');
        params.set('page_size', '20');

        const response = await authFetch(`${API_BASE}/api/admin/orders?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const data = await response.json();
        setOrders(data.items || []);
        setPagination({
          total: data.total,
          page: data.page,
          totalPages: data.total_pages,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, isAdmin, statusFilter, serviceFilter, debouncedSearch, searchParams]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (serviceFilter) params.set('service', serviceFilter);
    if (searchQuery) params.set('search', searchQuery);
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    setSearchParams(params, { replace: true });
  }, [statusFilter, serviceFilter, searchQuery, pagination.page, setSearchParams]);

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Access denied
  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl text-white mb-4">Access Denied</h1>
        <p className="text-[#E8E4DE]/60 mb-6">You must be an admin to view this page.</p>
        <Button asChild>
          <Link to="/">Return Home</Link>
        </Button>
      </div>
    );
  }

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-light text-white tracking-wide">Admin Orders</h1>
              <p className="text-[#E8E4DE]/60 mt-1">
                Manage all orders • {pagination.total} total
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button onClick={() => setNewOrderOpen(true)} className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="new-order-for-client-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Add Client / New Order for Client
              </Button>
              <Link
                to="/admin/clients"
                className="flex items-center gap-2 border border-white/15 text-[#E8E4DE] px-4 py-2 rounded-lg hover:bg-white/5 transition-colors"
                data-testid="admin-clients-link"
              >
                <Users className="h-4 w-4" /> Clients
              </Link>
              <Link
                to="/admin/portfolio"
                className="flex items-center gap-2 bg-[#C6A14A]/10 border border-[#C6A14A]/30 text-[#C6A14A] px-4 py-2 rounded-lg hover:bg-[#C6A14A]/20 transition-colors"
                data-testid="admin-portfolio-link"
              >
                <ImageIcon className="h-4 w-4" />
                Manage Portfolio
              </Link>
            </div>
          </div>
          <AddClientDialog open={newOrderOpen} onOpenChange={setNewOrderOpen} goToNewOrder />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="bg-[#0f1724] border-white/10 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E8E4DE]/40" />
                  <Input
                    placeholder="Search by address or agent..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[160px] bg-[#050816]/50 border-white/10 text-white" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1724] border-white/10">
                  <SelectItem value="all" className="text-white">All Statuses</SelectItem>
                  <SelectItem value="requested" className="text-white">Requested</SelectItem>
                  <SelectItem value="scheduled" className="text-white">Scheduled</SelectItem>
                  <SelectItem value="shot" className="text-white">Shot</SelectItem>
                  <SelectItem value="editing" className="text-white">Editing</SelectItem>
                  <SelectItem value="delivered" className="text-white">Delivered</SelectItem>
                  <SelectItem value="paid" className="text-white">Paid</SelectItem>
                  <SelectItem value="canceled" className="text-white">Canceled</SelectItem>
                </SelectContent>
              </Select>

              {/* Service Type Filter */}
              <Select value={serviceFilter || "all"} onValueChange={(v) => setServiceFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] bg-[#050816]/50 border-white/10 text-white" data-testid="service-filter">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1724] border-white/10">
                  <SelectItem value="all" className="text-white">All Services</SelectItem>
                  <SelectItem value="real_estate" className="text-white">Real Estate</SelectItem>
                  <SelectItem value="vacant_land" className="text-white">Vacant Land</SelectItem>
                  <SelectItem value="photo_restoration" className="text-white">Photo Restoration</SelectItem>
                  <SelectItem value="lora_training" className="text-white">LoRA Training</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="bg-red-500/10 border-red-500/30 mb-6">
            <CardContent className="pt-6">
              <p className="text-red-400">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
          </div>
        )}

        {/* Orders List */}
        {!loading && orders.length === 0 && (
          <Card className="bg-[#0f1724] border-white/10">
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-[#E8E4DE]/30 mx-auto mb-4" />
              <p className="text-[#E8E4DE]/60">No orders found</p>
            </CardContent>
          </Card>
        )}

        {!loading && orders.length > 0 && (
          <div className="space-y-3">
            {orders.map((order) => {
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
              const isLand = order.service_type === 'vacant_land';
              const packageName = isLand ? 'Vacant Land' : packageLabel(order.package);

              return (
                <Card
                  key={order.id}
                  className="bg-[#0f1724] border-white/10 hover:border-[#C6A14A]/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/real-estate-orders/${order.id}`)}
                  data-testid={`order-card-${order.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Order Number */}
                      <div className="w-[100px] shrink-0">
                        <span className="text-[#C6A14A] font-mono text-sm">
                          {formatOrderNumber(order.id)}
                        </span>
                      </div>

                      {/* Address */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Home className="h-4 w-4 text-[#E8E4DE]/40 shrink-0" />
                          <span className="text-white font-medium truncate">
                            {order.property_address || order.title || 'No address'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-[#E8E4DE]/50">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {order.user_email || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(order.preferred_date)}
                          </span>
                        </div>
                      </div>

                      {/* Package */}
                      <div className="w-[140px] shrink-0 text-right">
                        <span className="text-sm text-[#E8E4DE]/70">{packageName}</span>
                        <p className="text-[#C6A14A] font-medium">
                          {formatPrice(order.total_price_cents, order.is_custom_quote)}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="w-[100px] shrink-0">
                        <Badge className={statusConfig.className}>
                          {statusConfig.label}
                        </Badge>
                        {isLand && order.payment_state === 'paid' && (
                          <Badge className="mt-1 bg-emerald-500/15 text-emerald-300 border-emerald-500/30" data-testid={`order-prepaid-${order.id}`}>Paid</Badge>
                        )}
                      </div>

                      {/* View */}
                      <ExternalLink className="h-4 w-4 text-[#E8E4DE]/30" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="border-white/10 text-[#E8E4DE]"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-[#E8E4DE]/60 text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="border-white/10 text-[#E8E4DE]"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
